import type { WorkflowDefinitionDocument } from './platform-workflow-definition-lifecycle.models';

export type DraftRecoveryChoice = 'local' | 'latest';
export interface DraftRecoveryConflict {
  path: string;
  label: string;
  kind: 'value' | 'delete-edit' | 'order';
  base: unknown;
  latest: unknown;
  local: unknown;
  basePresent: boolean;
  latestPresent: boolean;
  localPresent: boolean;
  choice?: DraftRecoveryChoice;
}
export interface DraftRecoveryResult<T> {
  /** Display only until every conflict has an explicit choice. */
  preview: T;
  document: T | null;
  conflicts: DraftRecoveryConflict[];
  unresolvedPaths: string[];
}

const missing = Symbol('missing');
const owns = (object: object, key: string) =>
  Object.prototype.hasOwnProperty.call(object, key);
const record = (value: unknown): value is Record<string, unknown> =>
  value !== null &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  [Object.prototype, null].includes(Object.getPrototypeOf(value));
const read = (value: Record<string, unknown>, key: string) =>
  owns(value, key) ? value[key] : missing;
const propertyPath = (path: string, key: string) =>
  /^[A-Za-z_$][\w$]*$/.test(key)
    ? `${path}.${key}`
    : `${path}[${JSON.stringify(key)}]`;
const label = (key: string) =>
  key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[_-]/g, ' ');
const clone = (value: unknown): unknown =>
  Array.isArray(value)
    ? value.map(clone)
    : record(value)
      ? Object.fromEntries(
          Object.entries(value).map(([key, item]) => [key, clone(item)]),
        )
      : value;

function equal(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) return true;
  if (Array.isArray(left) && Array.isArray(right))
    return (
      left.length === right.length &&
      left.every((item, index) => equal(item, right[index]))
    );
  if (record(left) && record(right))
    return (
      Object.keys(left).length === Object.keys(right).length &&
      Object.keys(left).every(
        (key) => owns(right, key) && equal(left[key], right[key]),
      )
    );
  return false;
}

type ArrayKey = { field: string; key: (item: unknown) => string | null };
function capabilityKey(value: unknown): string | null {
  if (!record(value) || !owns(value, 'key') || !record(value['key']))
    return null;
  const key = value['key'];
  return ['provider', 'kind', 'code'].every(
    (field) => owns(key, field) && typeof key[field] === 'string' && key[field],
  ) &&
    owns(key, 'version') &&
    Number.isInteger(key['version']) &&
    Number(key['version']) > 0
    ? `${key['provider']}/${key['kind']}/${key['code']}@${key['version']}`
    : null;
}
function arrayKey(arrays: unknown[][]): ArrayKey | null {
  // Workflow collections are bounded. Larger or unidentifiable collections require a
  // whole-field choice; indices, labels and fuzzy equality are never identities.
  if (arrays.some((items) => items.length > 1000)) return null;
  const choices: ArrayKey[] = [
    ...['id', 'instanceId'].map((field) => ({
      field,
      key: (item: unknown) => {
        const value = record(item) ? read(item, field) : missing;
        return typeof value === 'string' && value.length > 0 ? value : null;
      },
    })),
    {
      field: 'capability',
      key: (item) =>
        record(item) && owns(item, 'capability')
          ? capabilityKey(item['capability'])
          : null,
    },
    { field: 'key', key: capabilityKey },
  ];
  const valid = (candidate: ArrayKey, items: unknown[]) => {
    const keys = items.map(candidate.key);
    return (
      keys.every((key) => key !== null) && new Set(keys).size === keys.length
    );
  };
  const candidate = choices.find((choice) =>
    valid(choice, arrays.find((items) => items.length > 0) ?? []),
  );
  return candidate && arrays.every((items) => valid(candidate, items))
    ? candidate
    : null;
}

const identityFields = [
  'scopeCode',
  'scopeTypeCode',
  'scopeKey',
  'nodeId',
  'typeCode',
  'stepCode',
  'provider',
  'code',
  'version',
  'settingsVersion',
  'capability',
  'command',
  'key',
  'inputSchemaHash',
  'outputSchemaHash',
  'contractHash',
];
function identityChanged(
  base: Record<string, unknown>,
  side: Record<string, unknown>,
): boolean {
  return identityFields.some(
    (key) =>
      (owns(base, key) || owns(side, key)) &&
      !equal(read(base, key), read(side, key)),
  );
}

/** Pure reconciliation of JSON-shaped draft snapshots. It never saves or overwrites latest. */
export function reconcileWorkflowDraft<T>(
  base: T,
  latest: T,
  local: T,
  choices: Readonly<Record<string, DraftRecoveryChoice>> = {},
): DraftRecoveryResult<T> {
  const conflicts: DraftRecoveryConflict[] = [];
  const conflict = (
    before: unknown,
    remote: unknown,
    ours: unknown,
    path: string,
    labels: string[],
    kind: DraftRecoveryConflict['kind'] = 'value',
  ): unknown => {
    const choice =
      owns(choices, path) && ['local', 'latest'].includes(choices[path])
        ? choices[path]
        : undefined;
    conflicts.push({
      path,
      label: labels.join(' › ') || 'Workflow',
      kind,
      base: before === missing ? undefined : clone(before),
      latest: remote === missing ? undefined : clone(remote),
      local: ours === missing ? undefined : clone(ours),
      basePresent: before !== missing,
      latestPresent: remote !== missing,
      localPresent: ours !== missing,
      choice,
    });
    return clone(choice === 'local' ? ours : remote);
  };
  const merge = (
    before: unknown,
    remote: unknown,
    ours: unknown,
    path: string,
    labels: string[],
    keyedItem = false,
  ): unknown => {
    if (equal(ours, remote)) return clone(ours);
    if (equal(ours, before)) return clone(remote);
    if (equal(remote, before)) return clone(ours);
    if (before === missing || remote === missing || ours === missing)
      return conflict(
        before,
        remote,
        ours,
        path,
        labels,
        before !== missing ? 'delete-edit' : 'value',
      );
    if (record(before) && record(remote) && record(ours)) {
      // A stable ID does not authorize combining edits across a changed node kind,
      // policy attachment/scope or exact capability binding.
      if (
        (keyedItem || (/\.automation$/.test(path) && record(before['command']) && record(remote['command']) && record(ours['command']))) &&
        (identityChanged(before, remote) || identityChanged(before, ours))
      )
        return conflict(before, remote, ours, path, labels);
      return Object.fromEntries(
        [
          ...new Set([
            ...Object.keys(remote),
            ...Object.keys(ours),
            ...Object.keys(before),
          ]),
        ]
          .map(
            (key) =>
              [
                key,
                merge(
                  read(before, key),
                  read(remote, key),
                  read(ours, key),
                  propertyPath(path, key),
                  [...labels, label(key)],
                ),
              ] as const,
          )
          .filter(([, value]) => value !== missing),
      );
    }
    if (Array.isArray(before) && Array.isArray(remote) && Array.isArray(ours)) {
      const identity = arrayKey([before, remote, ours]);
      if (!identity) return conflict(before, remote, ours, path, labels);
      const indexes = [before, remote, ours].map(
        (items) => new Map(items.map((item) => [identity.key(item)!, item])),
      );
      const [baseIndex, remoteIndex, localIndex] = indexes;
      const content = new Map<string, unknown>();
      for (const key of new Set(
        indexes.flatMap((index) => [...index.keys()]),
      )) {
        const sample =
          remoteIndex.get(key) ?? localIndex.get(key) ?? baseIndex.get(key);
        const name =
          record(sample) && typeof sample['displayName'] === 'string'
            ? sample['displayName']
            : key;
        const merged = merge(
          baseIndex.has(key) ? baseIndex.get(key) : missing,
          remoteIndex.has(key) ? remoteIndex.get(key) : missing,
          localIndex.has(key) ? localIndex.get(key) : missing,
          `${path}[${identity.field}=${encodeURIComponent(key)}]`,
          [...labels, name],
          true,
        );
        if (merged !== missing) content.set(key, merged);
      }
      const [baseOrder, remoteOrder, localOrder] = indexes.map((index) =>
        [...index.keys()].filter((key) => content.has(key)),
      );
      const common = new Set(remoteOrder.filter((key) => localIndex.has(key)));
      const baseCommon = new Set(baseOrder.filter((key) => common.has(key)));
      const baseline = baseOrder.filter((key) => baseCommon.has(key));
      const remoteExisting = remoteOrder.filter((key) => baseCommon.has(key));
      const localExisting = localOrder.filter((key) => baseCommon.has(key));
      const remoteReordered = !equal(baseline, remoteExisting);
      const localReordered = !equal(baseline, localExisting);
      const remotePosition = new Map(
        remoteOrder.map((key, index) => [key, index]),
      );
      const localPosition = new Map(
        localOrder.map((key, index) => [key, index]),
      );
      const addedTogether = [...common].filter((key) => !baseIndex.has(key));
      const placementConflict = addedTogether.some((key) =>
        [...common].some(
          (other) =>
            other !== key &&
            remotePosition.get(key)! < remotePosition.get(other)! !==
              localPosition.get(key)! < localPosition.get(other)!,
        ),
      );
      const reversedInsertionAnchors = (source: string[], other: string[]) =>
        source.some((key, index) => {
          if (baseIndex.has(key)) return false;
          const prior = source
            .slice(0, index)
            .reverse()
            .find((id) => baseCommon.has(id));
          const next = source.slice(index + 1).find((id) => baseCommon.has(id));
          return (
            prior !== undefined &&
            next !== undefined &&
            other.indexOf(prior) > other.indexOf(next)
          );
        });
      let primary = localReordered ? localOrder : remoteOrder;
      let secondary = localReordered ? remoteOrder : localOrder;
      if (
        (remoteReordered &&
          localReordered &&
          !equal(remoteExisting, localExisting)) ||
        placementConflict ||
        reversedInsertionAnchors(remoteOrder, localOrder) ||
        reversedInsertionAnchors(localOrder, remoteOrder)
      ) {
        const selected = conflict(
          baseOrder,
          remoteOrder,
          localOrder,
          `${path}.$order`,
          [...labels, 'order'],
          'order',
        );
        primary = selected as string[];
        secondary =
          owns(choices, `${path}.$order`) &&
          choices[`${path}.$order`] === 'local'
            ? remoteOrder
            : localOrder;
      }
      const order = [...primary];
      // Keep additions/restored entries from the other side, anchored to its nearest
      // surviving predecessor or successor. No entry is dropped by an order choice.
      for (const key of secondary) {
        if (order.includes(key)) continue;
        const position = secondary.indexOf(key);
        const predecessor = secondary
          .slice(0, position)
          .reverse()
          .find((item) => order.includes(item));
        const successor = secondary
          .slice(position + 1)
          .find((item) => order.includes(item));
        const at =
          predecessor !== undefined
            ? order.indexOf(predecessor) + 1
            : successor !== undefined
              ? order.indexOf(successor)
              : order.length;
        order.splice(at, 0, key);
      }
      return order.map((key) => content.get(key));
    }
    return conflict(before, remote, ours, path, labels);
  };
  const preview = merge(base, latest, local, '$', []) as T;
  const unresolvedPaths = conflicts
    .filter((item) => !item.choice)
    .map((item) => item.path);
  return {
    preview,
    document: unresolvedPaths.length ? null : preview,
    conflicts,
    unresolvedPaths,
  };
}

/** A recovery copy is untrusted browser storage, not a validated workflow. This
 * checks the shapes the editor reads; codes, references and readiness still go
 * through normal server validation. Unknown JSON fields are deliberately kept. */
export function isRecoverableWorkflowDocument(
  value: unknown,
  workflowCode: string,
): value is WorkflowDefinitionDocument {
  let remaining = 100_000;
  const json = (item: unknown, depth = 0): boolean => {
    if (--remaining < 0 || depth > 64) return false;
    if (item === null || typeof item === 'string' || typeof item === 'boolean')
      return true;
    if (typeof item === 'number') return Number.isFinite(item);
    if (Array.isArray(item))
      return item.every((child) => json(child, depth + 1));
    return (
      record(item) &&
      Object.values(item).every((child) => json(child, depth + 1))
    );
  };
  if (!record(value) || !json(value)) return false;
  const strings = (item: Record<string, unknown>, keys: string[]) =>
    keys.every((key) => owns(item, key) && typeof item[key] === 'string');
  const optional = (item: unknown, check: (item: unknown) => boolean) =>
    item == null || check(item);
  const list = (item: unknown, check: (item: unknown) => boolean) =>
    Array.isArray(item) && item.every(check);
  const text = (item: unknown) => typeof item === 'string';
  const number = (item: unknown) =>
    typeof item === 'number' && Number.isFinite(item);
  const capability = (item: unknown): boolean =>
    record(item) &&
    record(item['key']) &&
    strings(item['key'], ['provider', 'kind', 'code']) &&
    number(item['key']['version']) &&
    strings(item, ['inputSchemaHash', 'outputSchemaHash', 'contractHash']);
  const notification = (item: unknown): boolean =>
    record(item) &&
    strings(item, ['id', 'recipientTypeCode', 'severityCode']) &&
    optional(item['recipientTargetKey'], text);
  const notifications = (item: unknown) =>
    optional(item, (items) =>
      list(items, (child) => optional(child, notification)),
    );
  const decision = (item: unknown): boolean =>
    record(item) &&
    strings(item, ['decisionKind']) &&
    optional(item['requiredOutcomeCode'], text) &&
    optional(item['ownerQueueCode'], text) &&
    optional(item['eligibleRoleCodes'], (items) =>
      list(items, (child) => optional(child, text)),
    ) &&
    optional(
      item['reaction'],
      (child) =>
        record(child) &&
        strings(child, ['reactionCode']) &&
        optional(child['targetStateCode'], text),
    ) &&
    notifications(item['notifications']);
  const decisions = (item: unknown) =>
    optional(item, (items) =>
      list(items, (child) => optional(child, decision)),
    );
  const transition = (item: unknown): boolean =>
    record(item) &&
    strings(item, ['fromStateCode', 'toStateCode']) &&
    optional(item['requiredDecisionKinds'], (items) => list(items, text)) &&
    decisions(item['requiredDecisions']) &&
    notifications(item['notifications']);
  const transitions = (item: unknown) =>
    optional(item, (items) =>
      list(items, (child) => optional(child, transition)),
    );
  const declaration = (item: unknown): boolean => {
    if (!record(item) || !record(item['behavior']) || !record(item['detail']))
      return false;
    const behavior = item['behavior'],
      detail = item['detail'];
    return (
      optional(behavior['assignmentPolicyId'], text) &&
      list(
        behavior['commands'],
        (command) =>
          record(command) &&
          capability(command['capability']) &&
          list(command['requirements'], text) &&
          list(
            command['outcomes'],
            (outcome) =>
              record(outcome) &&
              strings(outcome, [
                'code',
                'label',
                'reasonPolicy',
                'destinationNodeId',
              ]),
          ) &&
          optional(command['inputBindings'], (items) =>
            list(
              items,
              (binding) =>
                record(binding) &&
                strings(binding, ['input', 'processDataKey']) &&
                optional(binding['projection'], text),
            ),
          ) &&
          optional(command['outputBindings'], (items) =>
            list(
              items,
              (binding) =>
                record(binding) &&
                strings(binding, ['output', 'processDataKey']),
            ),
          ),
      ) &&
      strings(detail, ['layout', 'contextPlacement']) &&
      list(
        detail['blocks'],
        (block) =>
          record(block) &&
          strings(block, [
            'instanceId',
            'provider',
            'code',
            'region',
            'width',
          ]) &&
          number(block['version']) &&
          number(block['settingsVersion']) &&
          record(block['settings']),
      ) &&
      optional(
        detail['mainInteraction'],
        (main) =>
          record(main) &&
          strings(main, ['code']) &&
          number(main['version']) &&
          capability(main['command']),
      ) &&
      optional(item['dashboards'], (dashboards) =>
        list(
          dashboards,
          (dashboard) =>
            record(dashboard) &&
            strings(dashboard, ['id', 'label', 'scope']) &&
            capability(dashboard['view']) &&
            record(dashboard['defaults']) &&
            ['columns', 'filterFields', 'summaries'].every((key) =>
              list(dashboard[key], text),
            ) &&
            list(dashboard['quickActions'], capability),
        ),
      )
    );
  };
  const policy = (item: unknown): boolean =>
    record(item) &&
    optional(item['assignmentBasisCode'], (basis) => basis === 'roles') &&
    strings(item, [
      'id',
      'scopeCode',
      'initialQueueCode',
      'assignmentModeCode',
      'unassignedBehaviorCode',
      'fallbackBehaviorCode',
      'returnBehaviorCode',
      'reentryBehaviorCode',
    ]) &&
    optional(item['nodeId'], text) &&
    ['eligibleRoleCodes', 'eligibleQueueCodes'].every((key) =>
      optional(item[key], (items) =>
        list(items, (child) => optional(child, text)),
      ),
    );
  const action = (item: unknown): boolean =>
    record(item) &&
    strings(item, [
      'id',
      'typeCode',
      'group',
      'mode',
      'failurePolicy',
      'caller',
    ]) &&
    number(item['order']) &&
    typeof item['requiredForProgression'] === 'boolean' &&
    capability(item['command']) &&
    list(item['outcomes'], text) &&
    list(
      item['inputs'],
      (input) =>
        record(input) &&
        strings(input, ['field', 'source']) &&
        optional(input['context'], text),
    );
  return (
    strings(value, [
      'payloadSchemaVersion',
      'workflowCode',
      'definitionVersionCode',
      'scopeTypeCode',
      'configurationCode',
      'configurationVersionCode',
      'instanceCardinalityCode',
    ]) &&
    (['1.10', '1.11', '1.12', '1.13', '1.14', '1.15', '1.16', '1.17', '1.18', '1.19', '1.20', '1.21', '1.22'].includes(
      String(value['payloadSchemaVersion']),
    )
      ? optional(value['subjectTypeCode'], text)
      : text(value['subjectTypeCode'])) &&
    optional(value['displayName'], text) &&
    optional(value['description'], text) &&
    optional(
      value['processData'],
      (data) =>
        record(data) &&
        list(
          data['fields'],
          (field) =>
            record(field) &&
            strings(field, ['key', 'label', 'type']) &&
            optional(
              field['requiredAtStart'],
              (required) => typeof required === 'boolean',
            ) &&
            optional(field['recordTypeCode'], text),
        ),
    ) &&
    value['workflowCode'] === workflowCode &&
    optional(value['scopeKey'], text) &&
    list(
      value['nodes'],
      (node) =>
        record(node) &&
        strings(node, ['id', 'typeCode']) &&
        number(node['displayOrder']) &&
        optional(node['stepCode'], text) &&
        optional(node['displayName'], text) &&
        transitions(node['allowedTransitions']) &&
        decisions(node['requiredDecisions']) &&
        optional(node['declaration'], declaration) &&
        optional(node['automation'], activity => record(activity) && activity['version'] === 1 && capability(activity['command']) && list(activity['inputs'], input => record(input) && strings(input, ['field','source']) && optional(input['context'], text) && optional(input['literalJson'], text)) && list(activity['outcomes'], outcome => record(outcome) && strings(outcome, ['commandOutcome','routeOutcome']))) &&
        optional(
          node['routing'],
          (routing) =>
            record(routing) &&
            strings(routing, ['conditionCode']) &&
            number(routing['conditionVersion']),
        ),
    ) &&
    list(
      value['edges'],
      (edge) =>
        record(edge) &&
        strings(edge, ['id', 'fromNodeId', 'toNodeId']) &&
        optional(edge['outcomeCode'], text) &&
        optional(edge['pathKindCode'], text),
    ) &&
    transitions(value['workflowTransitions']) &&
    decisions(value['requiredDecisions']) &&
    optional(value['assignmentPolicies'], (items) =>
      list(items, (child) => optional(child, policy)),
    ) &&
    optional(value['capabilityReferences'], (items) =>
      list(items, capability),
    ) &&
    optional(value['triggers'], (items) =>
      list(
        items,
        (trigger) =>
          record(trigger) &&
          strings(trigger, ['id', 'kind', 'nodeId']) &&
          optional(trigger['edgeId'], text) &&
          optional(trigger['outcomeCode'], text) &&
          list(trigger['actions'], action),
      ),
    ) &&
    optional(
      value['numbering'],
      (numbering) =>
        record(numbering) &&
        strings(numbering, ['mode']) &&
        ['policyId', 'agencyCode', 'programCode'].every((key) =>
          optional(numbering[key], text),
        ),
    ) &&
    optional(
      value['packReference'],
      (pack) =>
        record(pack) &&
        strings(pack, ['code', 'manifestHash']) &&
        number(pack['version']),
    )
  );
}
